/**
 * Shared formatting utilities for currency and numbers.
 */

export function formatCurrency(amount: number | string | null | undefined): string {
  const num = Number(amount)
  if (isNaN(num)) return '$0.00'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

/** Format a number with locale grouping (no currency symbol) */
export function formatNumber(amount: number | string | null | undefined, decimals = 0): string {
  const num = Number(amount)
  if (isNaN(num)) return '0'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}

/** Safely coerce a value to number, returning 0 for NaN/null/undefined */
export function safeNum(val: unknown): number {
  const n = Number(val)
  return isNaN(n) ? 0 : n
}

/** Safely parse a date string (handles SQLite 'YYYY-MM-DD HH:MM:SS' format) */
export function safeDate(val: string | null | undefined): Date | null {
  if (!val) return null
  const d = new Date(val.includes('T') ? val : val.replace(' ', 'T'))
  return isNaN(d.getTime()) ? null : d
}

/** Format a date for display, returns empty string for invalid dates */
export function formatDate(val: string | null | undefined): string {
  const d = safeDate(val)
  if (!d) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Format a relative time string like '5m ago', 'yesterday', etc. */
export function formatRelative(val: string | null | undefined): string {
  const d = safeDate(val)
  if (!d) return ''
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}
